import * as React from "react";

import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "bernie-view";

import { UitkPrimaryButton } from "uitk-react-button";
import { UitkFigureAspectRatioType as ExperimentalAspectRatioType } from "uitk-react-experimental-images";
import { UitkText } from "uitk-react-text";

import { FlexComponentProps, FlexComponent } from "typings/flexFramework/FlexComponent";
import { FlexModuleResult } from "typings/microserviceModels/content-flex-module";

import { ScrimCard } from "components/shared/ScrimCard/ScrimCard";
import { withMabTracker, MabTrackEvent } from "src/components/utility/analytics/MabTracker";
import { Experiment, isVariantEnabled } from "src/components/utility/experiment";
import { UitkScrimType as UitkExperimentalScrimType } from "uitk-react-experimental-scrim";

export interface EditorialBannerFlexComponent extends FlexComponent {
  model: FlexModuleResult;
}

export interface EditorialBannerProps extends FlexComponentProps {
  flexComponent: EditorialBannerFlexComponent;
  mabTrack?: (x: MabTrackEvent) => null;
}

export const EditorialBanner = withMabTracker((props: EditorialBannerProps) => {
  const { mabTrack, flexComponent, context } = props;
  const { model, fmId } = flexComponent;
  const [{ media, text }] = model.items;

  /**
   * This is a temporary work around due to limitations of the call-to-action model that will be addressed in the future.
   * Parsing data from contributed HTML content should not be considered an accepted approach to be repeated elsewhere
   */
  const htmlText = text as string;
  const [, title] = /<h3>(.*?)<\/h3>/g.exec(htmlText);
  const [, subtitle] = /<p class=\"subtitle\">(.*?)<\/p>/g.exec(htmlText);
  const [, ctaLabel] = /<p><button>(.*?)<\/button><\/p>/g.exec(htmlText);

  // Media content
  const [desktopMedia, mobileMedia] = media;
  const { mediaUrl: desktopMediaURL } = desktopMedia;
  const { mediaUrl: mobileMediaURL } = mobileMedia;
  const { clickUrl } = desktopMedia;

  // Component display properties
  const className = "EditorialBanner all-x-margin-three all-t-margin-three";

  const cardClicked = mabTrack
    ? () => {
        const mabTrackData: MabTrackEvent = {
          moduleName: "List_your_property",
          eventTarget: "List_your_property",
          eventType: "homepageClick.nonPath",
          rfrr: "LYP_Homepage",
        };
        mabTrack(mabTrackData);
      }
    : undefined;

  const scrimCardProps = {
    id: `editorial-banner-${fmId}`,
    className,
    titleSlot: <h3 className="uitk-type-800">{title}</h3>,
    subtitleSlot: <UitkText size={500}>{subtitle}</UitkText>,
    footerSlot: (
      <div className="all-b-margin-one">
        <UitkPrimaryButton tag="a" onClick={cardClicked} href={clickUrl} className="all-y-margin-three">
          {ctaLabel}
        </UitkPrimaryButton>
      </div>
    ),
  };

  const isVariantScrimTestEnabled = isVariantEnabled(context, Experiment.PWA_banner_VR_scrims_test);
  const scrimTypeForSmallView = isVariantScrimTestEnabled
    ? UitkExperimentalScrimType.OVERLAY_TOP
    : UitkExperimentalScrimType.OVERLAY;
  const scrimTypeForMediumAndBiggerViews = isVariantScrimTestEnabled
    ? UitkExperimentalScrimType.OVERLAY_LEFT
    : UitkExperimentalScrimType.OVERLAY;

  return (
    <Viewport>
      <ViewSmall>
        <ScrimCard
          {...scrimCardProps}
          aspectRatio={ExperimentalAspectRatioType.R4_5}
          backgroundImageURL={mobileMediaURL}
          scrimType={scrimTypeForSmallView}
        />
      </ViewSmall>
      <ViewMedium>
        <ScrimCard
          {...scrimCardProps}
          aspectRatio={ExperimentalAspectRatioType.R16_9}
          backgroundImageURL={desktopMediaURL}
          scrimType={scrimTypeForMediumAndBiggerViews}
        />
      </ViewMedium>
      <ViewLarge>
        <ScrimCard
          {...scrimCardProps}
          aspectRatio={ExperimentalAspectRatioType.R3_1}
          backgroundImageURL={desktopMediaURL}
          scrimType={scrimTypeForMediumAndBiggerViews}
        />
      </ViewLarge>
    </Viewport>
  );
});

export default EditorialBanner;
